import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Eye, EyeOff, GripVertical, Clock } from "lucide-react";

export default function TickerTab({ snapshot }) {
  const tickers = snapshot.tickers || [];
  const showNext = snapshot.settings.ticker_show_next_match !== false;
  const [text, setText] = useState("");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/tickers", { text: text.trim() });
      setText("");
      toast.success("Ticker-Text hinzugefügt");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const update = async (id, patch) => { try { await api.put(`/tickers/${id}`, patch); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const remove = async (id) => { await api.delete(`/tickers/${id}`); toast.success("Entfernt"); };
  const toggleNext = async () => { try { await api.put("/settings", { ticker_show_next_match: !showNext }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const arr = [...tickers];
    const from = arr.findIndex((t) => t.id === dragId);
    const to = arr.findIndex((t) => t.id === targetId);
    if (from === -1 || to === -1) return;
    const [mv] = arr.splice(from, 1);
    arr.splice(to, 0, mv);
    setDragId(null);
    setOverId(null);
    await api.post("/tickers/reorder", { ordered_ids: arr.map((t) => t.id) });
  };

  return (
    <div className="space-y-6">
      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Live-Ticker</h2>
        <p className="text-xs text-[#8A99AD] mb-4">Läuft in allen Modi unten durch. Mehrere aktive Texte werden nacheinander angezeigt.</p>

        <button onClick={toggleNext} data-testid="toggle-next-match-btn" className="flex items-center gap-3 bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-3 hover:border-[#37456b] transition-colors mb-5">
          <span className={`w-11 h-6 rounded-full relative transition-colors ${showNext ? "bg-[#00F0FF]" : "bg-[#1E2638]"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${showNext ? "left-[22px]" : "left-0.5"}`} />
          </span>
          <Clock className="w-4 h-4 text-[#8A99AD]" />
          <span className="text-sm">Nächstes Spiel (nach Datum &amp; Uhrzeit) im Ticker anzeigen</span>
        </button>

        <div className="flex flex-wrap gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Neuer Ticker-Text"
            data-testid="ticker-text-input"
            className="bg-[#0A0C10] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] text-sm flex-1 min-w-[240px]"
          />
          <button onClick={add} data-testid="add-ticker-btn" className="flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors">
            <Plus className="w-4 h-4" /> Hinzufügen
          </button>
        </div>
      </section>

      <section className="bg-[#121620] border border-[#1E2638] rounded-2xl p-6">
        <h2 className="font-display font-bold uppercase tracking-wider text-lg mb-1">Texte & Reihenfolge</h2>
        <p className="text-xs text-[#8A99AD] mb-4">Zum Sortieren ziehen. Mit dem Auge ein-/ausblenden.</p>
        <div className="space-y-2">
          {tickers.map((t) => (
            <div
              key={t.id}
              draggable
              onDragStart={() => setDragId(t.id)}
              onDragOver={(e) => { e.preventDefault(); setOverId(t.id); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              onDrop={() => onDrop(t.id)}
              className={`flex items-center gap-3 bg-[#0A0C10] border rounded-lg p-3 transition-colors ${overId === t.id && dragId !== t.id ? "border-[#E5FF00]" : "border-[#1E2638]"} ${dragId === t.id ? "opacity-40" : ""}`}
              data-testid={`ticker-item-${t.id}`}
            >
              <span className="text-[#8A99AD] cursor-grab active:cursor-grabbing" data-testid={`ticker-drag-${t.id}`}>
                <GripVertical className="w-5 h-5" />
              </span>
              <input
                defaultValue={t.text}
                onBlur={(e) => e.target.value !== t.text && update(t.id, { text: e.target.value })}
                data-testid={`ticker-edit-${t.id}`}
                className={`flex-1 bg-transparent border-none outline-none text-sm ${t.active ? "text-white" : "text-[#8A99AD] line-through"}`}
              />
              <button onClick={() => update(t.id, { active: !t.active })} data-testid={`ticker-toggle-${t.id}`} className={`p-2 rounded-lg border ${t.active ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#1E2638] text-[#8A99AD]"}`}>
                {t.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(t.id)} data-testid={`ticker-delete-${t.id}`} className="p-2 rounded-lg text-[#8A99AD] hover:text-[#FF2A55]">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {tickers.length === 0 && <p className="text-[#8A99AD] text-sm">Noch keine Ticker-Texte.</p>}
        </div>
      </section>
    </div>
  );
}
